import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Check, Copy, Users, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationBell = ({ userId, onFamilyApprovalsClick }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [familyApprovalCount, setFamilyApprovalCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const panelRef = useRef(null);

  const fetchNotifications = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/notifications/user/${userId}?limit=20`);
      const data = await res.json();
      if (data.success) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch {}
  };

  const fetchFamilyApprovals = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/family/${userId}/approvals/pending`);
      const data = await res.json();
      if (data?.success) setFamilyApprovalCount(data.total || 0);
    } catch {}
  }, [userId]);

  useEffect(() => {
    fetchNotifications();
    fetchFamilyApprovals();
    const interval = setInterval(() => {
      fetchNotifications();
      fetchFamilyApprovals();
    }, 30000);
    return () => clearInterval(interval);
  }, [userId, fetchFamilyApprovals]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowPanel(false);
      }
    };
    if (showPanel) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPanel]);

  const markAllRead = async () => {
    if (!userId) return;
    try {
      await fetch(`${API_URL}/api/notifications/mark-all-read/${userId}`, { method: 'POST' });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  const copyPromoCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      // Fallback for mobile
      const textarea = document.createElement('textarea');
      textarea.value = code;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  };

  const getNotifIcon = (type) => {
    if (type === 'promo') return '🎫';
    if (type === 'message') return '💬';
    if (type === 'ride') return '🚗';
    if (type === 'family_safe_arrival') return '🏡';
    if (type === 'family_sos') return '🚨';
    if (type === 'family_approval' || type === 'family_approval_decision') return '👪';
    return '🔔';
  };

  const acknowledgeSafeArrival = async (notif) => {
    const meta = notif.metadata || {};
    const rideId = meta.ride_id;
    if (!userId || !rideId) return;
    // Optimistic update
    setNotifications(prev => prev.map(n =>
      n.id === notif.id
        ? { ...n, is_read: true, metadata: { ...(n.metadata || {}), acknowledged: true } }
        : n
    ));
    setUnreadCount(c => Math.max(0, c - (notif.is_read ? 0 : 1)));
    try {
      await fetch(`${API_URL}/api/family/${userId}/safe-arrival/${rideId}/acknowledge`, { method: 'POST' });
    } catch {
      // Re-sync on failure
      fetchNotifications();
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="relative flex items-center gap-1.5" ref={panelRef}>
      {/* Family approvals quick badge */}
      {familyApprovalCount > 0 && (
        <button
          type="button"
          onClick={() => {
            if (onFamilyApprovalsClick) onFamilyApprovalsClick();
            // Optimistic refresh after parent navigates
            setTimeout(fetchFamilyApprovals, 1500);
          }}
          data-testid="family-approval-pill"
          title={`${familyApprovalCount} family ride${familyApprovalCount === 1 ? '' : 's'} awaiting your approval`}
          className="flex items-center gap-1 px-2 py-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-semibold shadow-sm animate-pulse"
        >
          <Users className="w-3 h-3" />
          <span>Family</span>
          <span data-testid="family-approval-pill-count" className="bg-white text-rose-700 rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold">
            {familyApprovalCount > 9 ? '9+' : familyApprovalCount}
          </span>
        </button>
      )}

      <button
        onClick={() => setShowPanel(!showPanel)}
        data-testid="notification-bell"
        className="relative p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <Bell className="w-5 h-5 text-gray-700 dark:text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] px-1">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showPanel && (
        <div className="absolute right-0 top-12 w-80 max-h-96 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-[100]" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-green-600 dark:text-green-400 hover:underline flex items-center gap-1"
                  data-testid="mark-all-read-btn"
                >
                  <Check className="w-3 h-3" />
                  Mark all read
                </button>
              )}
              <button onClick={() => setShowPanel(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-72">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                No notifications yet
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                    !notif.is_read ? 'bg-green-50/50 dark:bg-green-900/10' : ''
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <span className="text-lg mt-0.5">{getNotifIcon(notif.type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notif.is_read ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                        {notif.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{notif.message}</p>
                      {notif.type === 'promo' && notif.data?.code && (
                        <button
                          onClick={() => copyPromoCode(notif.data.code)}
                          data-testid={`copy-promo-${notif.data.code}`}
                          className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 dark:bg-green-900/40 hover:bg-green-200 dark:hover:bg-green-800/60 rounded-md transition-colors"
                        >
                          <span className="font-mono text-xs font-bold text-green-800 dark:text-green-300">{notif.data.code}</span>
                          {copiedCode === notif.data.code ? (
                            <Check className="w-3 h-3 text-green-600" />
                          ) : (
                            <Copy className="w-3 h-3 text-green-600" />
                          )}
                          {copiedCode === notif.data.code && (
                            <span className="text-[10px] text-green-600 font-medium">Copied!</span>
                          )}
                        </button>
                      )}
                      {notif.type === 'family_safe_arrival' && notif.metadata?.requires_acknowledgement && !notif.metadata?.acknowledged && (
                        <button
                          onClick={() => acknowledgeSafeArrival(notif)}
                          data-testid={`safe-arrival-confirm-${notif.metadata?.ride_id || notif.id}`}
                          className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors text-[11px] font-semibold shadow-sm"
                        >
                          <ShieldCheck className="w-3 h-3" />
                          Confirm safe arrival
                        </button>
                      )}
                      {notif.type === 'family_safe_arrival' && notif.metadata?.acknowledged && (
                        <span className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded text-[10px] font-semibold" data-testid="safe-arrival-confirmed">
                          <Check className="w-3 h-3" />
                          Confirmed
                        </span>
                      )}
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">{formatTime(notif.created_at)}</p>
                    </div>
                    {!notif.is_read && (
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
